import React, {
  type FunctionComponent,
  Suspense,
  useEffect,
  useState,
} from 'react';

import { formatLocalDate } from '@iconik-io/core/src/date/format';
import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import {
  type FilesFormatSchemaWithId,
  httpGetAssetsByAssetIdVersionsByVersionIdKeyframes,
} from '@iconik-io/iconik-api/src/services/files';
import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';
import { httpGetFaceRecognitionPersonsByPersonId } from '@iconik-io/iconik-api/src/services/ml';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_PERSONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { useEntitySimple } from '@iconik-io/shared-lib/src/react/hooks/entity/use_entity_simple';
import { lazyComponent } from '@iconik-io/shared-lib/src/react/utils/lazy_component';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { selectAssetVersionExtended } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import {
  useAppDispatch,
  useAppSelector,
} from '@iconik-io/shared-lib/src/redux/redux_hooks';

import { jobsStatusMap, jobsStepStatusMap } from '../../constants/jobs';
import { ICONS } from '../../icons/icon';
import Button from '../../ui/button/button';
import IconButton from '../../ui/icon_button/icon_button';
import Label from '../../ui/label/label';
import { getRegularObjectLink } from '../../utils/get_object_link';
import AsyncUser from '../async_user/async_user';
import DebugDumpEntity from '../debug_dump_entity/debug_dump_entity';
import JobLog from '../job_log/job_log';
import JobType from '../job_type/job_type';
import { StorageName } from '../jobs_columns/storage_name/storage_name';
import { TranscoderName } from '../jobs_columns/transcoder_name/transcoder_name';
import { Link } from '../link/link';
import SearchPod from '../search_pod_detailed/search_pod_detailed';
import UserBubble from '../user_bubble/user_bubble';
import { getFormattedFileSize } from './get_formatted_file_size';

import styles from './job.module.css';

// Keeps Chart.js out of the initial bundle.
const JobChart = lazyComponent(
  () => import('../job_chart/job_chart'),
  'default',
);

const ALLOWED_THUMBNAIL_OBJECT_TYPES: string[] = [
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
];

const SUBTITLES_TRANSCODER_ID = '21ce91d2-f5ea-11e7-acd4-f45c89b15ba1';
const INTERNAL_TRANSCODER_ID = '0e512736-8818-11ec-a2e7-acde48001122';

type Props = {
  job: JobsJobElasticSchemaWithId;
  searchItem?: SearchItemEntity;
  originalFormat?: FilesFormatSchemaWithId;
  dateTimeFormat: string;
  onJobOpenClick?: () => void;
  deletedItems?: string[];
};

function useTranscoderName(
  sourceTranscoderId: string | undefined,
): string | undefined {
  const shouldHideTranscoderEntity =
    sourceTranscoderId === SUBTITLES_TRANSCODER_ID ||
    sourceTranscoderId === INTERNAL_TRANSCODER_ID;

  const transcoderId = shouldHideTranscoderEntity
    ? undefined
    : sourceTranscoderId;
  const displayName =
    sourceTranscoderId === SUBTITLES_TRANSCODER_ID ? 'Subtitles' : undefined;

  const { entity: transcoder } = useEntitySimple('transcoders', transcoderId);

  return transcoder?.name ?? displayName;
}

const Job: FunctionComponent<Props> = ({
  job,
  searchItem,
  originalFormat,
  dateTimeFormat,
  onJobOpenClick,
  deletedItems,
}) => {
  const dispatch = useAppDispatch();
  const [searchItemWithVersion, setSearchItemWithVersion] =
    useState(searchItem);
  const [loading, setLoading] = useState(false);

  const transcoderId =
    typeof job.metadata?.transcoder_id === 'string'
      ? job.metadata.transcoder_id
      : undefined;
  const transcoderDisplayName = useTranscoderName(transcoderId);

  const internetMediaType = originalFormat?.metadata?.[0]
    ?.internet_media_type as string | undefined;

  const status = jobsStatusMap[job.status];
  const [stepLogState, setStepLogState] = useState(
    {} as Record<number, boolean>,
  );

  const objectType = searchItem?.object_type;

  const shouldFetchPerson =
    job.object_type === OBJECT_TYPE_PERSONS &&
    job.object_id &&
    job.type !== 'DELETE_PERSON';
  const personId = shouldFetchPerson ? job.object_id : undefined;
  const person = useAppSelector((state) =>
    personId ? state.entities.persons[personId] : undefined,
  );
  const [personFetchFailed, setPersonFetchFailed] = useState(false);

  useEffect(() => {
    if (personId && !person && !personFetchFailed) {
      dispatch(httpGetFaceRecognitionPersonsByPersonId(personId)).catch(() => {
        setPersonFetchFailed(true);
      });
    }
  }, [personId, person, personFetchFailed]);

  const fileSize = getFormattedFileSize(job, searchItem);

  const searchItemId =
    typeof searchItem?.id === 'string' ? searchItem.id : undefined;

  const versionId =
    typeof job?.metadata?.version_id === 'string'
      ? job.metadata.version_id
      : undefined;

  const version = useAppSelector((state) =>
    selectAssetVersionExtended(state, searchItemId, versionId),
  );

  useEffect(() => {
    if (!searchItem || !searchItemId || !versionId) {
      return;
    }
    if (versionId) {
      setLoading(true);
    }
    dispatch(
      httpGetAssetsByAssetIdVersionsByVersionIdKeyframes(
        searchItemId,
        versionId,
      ),
    )
      .then(({ body }) => {
        const updatedSearchItem = {
          ...searchItem,
          versions_number: version?.versionNumber ?? searchItem.versions_number,
          keyframes: body.objects,
        };
        setSearchItemWithVersion(updatedSearchItem);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [searchItem, searchItemId, version, versionId]);

  return (
    <div>
      <div className={styles.head}>
        <div className={styles.thumbnail_progress}>
          {objectType &&
            ALLOWED_THUMBNAIL_OBJECT_TYPES.includes(objectType) &&
            searchItemWithVersion && (
              <SearchPod
                hideContextMenu
                podSize={{ width: 308, height: 164 }}
                searchItem={
                  deletedItems?.includes(searchItemWithVersion.id)
                    ? { ...searchItemWithVersion, status: 'DELETED' }
                    : searchItemWithVersion
                }
                getObjectLink={(id, objectTypeValue) => {
                  const base = getRegularObjectLink(
                    id,
                    objectTypeValue,
                    versionId,
                  );
                  if (job.type !== 'METADATA_FILLING') {
                    return base;
                  }
                  const search = new URLSearchParams({
                    open_panel: 'asset_metadata',
                    ...(job.status === 'PENDING_USER'
                      ? { job_id: job.id }
                      : {}),
                  }).toString();
                  return `${base}?${search}`;
                }}
                loading={loading}
              />
            )}
          {person && (
            <Link
              to={`/admin/face_recognition/persons/${person.id}`}
              className={styles.person_link}
            >
              <UserBubble
                name={person.name ?? ''}
                src={person.image_url || ''}
                size="xl"
                showTooltip={false}
              />
              <span>{person.name}</span>
            </Link>
          )}
          <div className={styles.chart}>
            <Suspense fallback={null}>
              <JobChart job={job} />
            </Suspense>
          </div>
        </div>
        {onJobOpenClick && (
          <IconButton
            variant="white"
            icon={ICONS.Expand}
            onClick={onJobOpenClick}
          />
        )}
      </div>

      <DebugDumpEntity entityKey="jobs" id={job.id} />

      <div className={styles.job_info}>
        {job.created_by ? (
          <>
            <div className={styles.created_by_label}>Started by:</div>
            <AsyncUser id={job.created_by}>
              {({ user }) => {
                if (!user.id) {
                  return null;
                }
                return (
                  <div className={styles.created_by_user}>
                    <UserBubble
                      size="sm"
                      src={user.photo}
                      name={getUserLabel(user)}
                      showTooltip={false}
                    />
                    {getUserLabel(user)}
                  </div>
                );
              }}
            </AsyncUser>
          </>
        ) : null}
        <div>Job Type:</div>
        <div>
          <JobType job={job} />
        </div>
        {transcoderDisplayName ? (
          <>
            <div>Transcoder:</div>
            <div>
              <TranscoderName transcoderName={transcoderDisplayName} />
            </div>
          </>
        ) : null}
        {job.metadata?.storage_id ? (
          <>
            <div>Storage:</div>
            <div>
              <StorageName storageId={job.metadata.storage_id as string} />
            </div>
          </>
        ) : null}
        {job.type === 'SCAN' ? (
          <>
            <div>Files discovered:</div>
            <div>{job.progress_processed}</div>
          </>
        ) : null}

        {job.started_at ? (
          <>
            <div>Started at:</div>
            <div>{formatLocalDate(job.started_at, dateTimeFormat)}</div>
          </>
        ) : null}

        {job.completed_at ? (
          <>
            <div>Completed at:</div>
            <div>{formatLocalDate(job.completed_at, dateTimeFormat)}</div>
          </>
        ) : null}

        {internetMediaType ? (
          <>
            <div>Media Type:</div>
            <div>{internetMediaType}</div>
          </>
        ) : null}
        {status ? (
          <>
            <div>Status:</div>
            <div>
              <Label variant={status.bsStyle}>{status.label}</Label>
            </div>
          </>
        ) : null}
        {fileSize ? (
          <>
            <div>File Size:</div>
            <div>{fileSize}</div>
          </>
        ) : null}
        {job.metadata?.client_name ? (
          <>
            <div>Client:</div>
            <div>
              {job.metadata.client_version
                ? `${job.metadata.client_name}@${job.metadata.client_version}`
                : (job.metadata.client_name as string)}
            </div>
          </>
        ) : null}
        <div>Date created:</div>
        <div>{formatLocalDate(job.date_created, dateTimeFormat)}</div>
        <div>Last updated:</div>
        <div>{formatLocalDate(job.date_modified, dateTimeFormat)}</div>
        {job.metadata?.original_storage_name ? (
          <>
            <div>Original storage:</div>
            <div>{job.metadata.original_storage_name as string}</div>
          </>
        ) : null}
        {job.metadata?.destination_storage_name ? (
          <>
            <div>Destination storage:</div>
            <div>{job.metadata.destination_storage_name as string}</div>
          </>
        ) : null}
        {job.error_message || job.message ? (
          <>
            <div className={styles.job_log_label}>Job Log:</div>
            <JobLog
              className={styles.job_log}
              message={`${job.message ?? ''}\n${job.error_message ?? ''}`}
            />
          </>
        ) : null}
      </div>
      {job.has_children && (
        <Link
          className={styles.subjobs_link}
          to={`/admin/jobs/history/${job.id}/subjobs`}
        >
          View subjobs
        </Link>
      )}
      <div className={styles.steps}>
        {job.steps?.map((step, stepIndex) => {
          const stepStatus = step.status
            ? jobsStepStatusMap[step.status]
            : undefined;
          const logs: string[] = [step.message, step.error_message].filter(
            (message) => message,
          ) as string[];
          return (
            <React.Fragment key={step.id}>
              <div>{step.label}</div>
              <div>{formatLocalDate(step.date_updated, dateTimeFormat)}</div>
              <div className={styles.step_status}>
                {stepStatus && (
                  <Label variant={stepStatus.bsStyle}>{stepStatus.label}</Label>
                )}
              </div>
              <div>
                {logs.length > 0 && (
                  <Button
                    size="s"
                    noBorder
                    variant="info"
                    className={styles.expand_button}
                    onClick={() =>
                      setStepLogState({
                        ...stepLogState,
                        [stepIndex]: !stepLogState[stepIndex],
                      })
                    }
                  >
                    {stepLogState[stepIndex] ? 'Hide logs' : 'Show logs'}
                  </Button>
                )}
              </div>
              {stepLogState[stepIndex] && logs.length > 0 && (
                <div className={styles.step_log}>
                  {logs.map((log, index) => (
                    <JobLog key={index} message={log} />
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Job;
